import { Slice } from '@atlaskit/editor-prosemirror/model';
import type { EditorState, Transaction } from '@atlaskit/editor-prosemirror/state';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';

import { aiSessionContextPluginKey } from '../../ai-session-context/ai-session-context-plugin-key';
import { aiExperienceDecorationPluginKey } from '../../decoration/decoration-plugin-key';
import { purePmInEditorDirectStreamingPluginKey } from '../pluginKey';
import { updateStreamPositions, metaTransaction } from '../transforms';
import type { PurePmInEditorDirectStreamingPluginState } from '../types';
import {
	resolveContentFragment,
	fragmentIsHealthy,
	calculateTelepointerPos,
} from '../utils/adfConversion';
import { clampPosition } from '../utils/positionValidation';

import type { FireAnalyticsStep } from './buildMountTransaction';
import {
	calculateEmptySelectionAdjustment,
	calculateBlockMarkAdjustment,
	calculateRangeSelectionAdjustment,
	calculateListAdjustment,
} from './positionAdjustments';
import { mapStartPos, mapToPos } from './positionMapping';
import {
	calculateTableAdjustment,
	calculateSyncBlockAdjustment,
	calculateSliceOpenStartEndAdjustment,
} from './sliceAdjustments';

/**
 * Builds transaction for processing a new content chunk.
 */
export function buildUpdateTransaction(
	state: EditorState,
	fireAnalyticsStep: FireAnalyticsStep,
): Transaction | null {
	const decorationPluginState = aiExperienceDecorationPluginKey.getState(state);
	const { positions } = decorationPluginState ?? {};
	const sessionState = aiSessionContextPluginKey.getState(state);
	if (!sessionState) {
		return null;
	}
	const { adfResponse, markdown, idMap, responseVersion } = sessionState;
	const streamingState = purePmInEditorDirectStreamingPluginKey.getState(state);

	if (!positions || !streamingState) {
		return null;
	}

	const { startPos, userContentEndPos, aiContentEndPos, posAdjusted, streamTimeStamp } =
		streamingState;

	if (startPos === undefined || userContentEndPos === undefined) {
		return buildPositionOnlyUpdate(state, streamingState, responseVersion);
	}

	// Resolve content using the prioritised conversion chain:
	// ADF → experiment-gated markdown → free-gen markdown fallback (with incomplete tag guard)
	const fragment = resolveContentFragment(state.schema, adfResponse, markdown, idMap, {
		skipIncompleteCustomTags: true,
	});
	if (!fragment) {
		return buildPositionOnlyUpdate(state, streamingState, responseVersion);
	}

	let slice = Slice.maxOpen(fragment);
	if (!slice.content.firstChild) {
		slice = Slice.empty;
	}

	if (slice.size <= 0) {
		return buildPositionOnlyUpdate(state, streamingState, responseVersion);
	}

	if (!fragmentIsHealthy(fragment)) {
		return buildPositionOnlyUpdate(state, streamingState, responseVersion);
	}

	const docSize = state.doc.content.size;
	let workingStartPos = clampPosition(startPos, docSize);
	let workingUserContentEndPos = clampPosition(userContentEndPos, docSize);
	let workingPosAdjusted = posAdjusted;

	// Position adjustments (once per stream)
	if (!posAdjusted) {
		const isEmptySelection = positions[0] === positions[1];

		if (isEmptySelection) {
			const adjustment = calculateEmptySelectionAdjustment({
				state,
				fragment,
				startPos: workingStartPos,
				userContentEndPos: workingUserContentEndPos,
			});
			if (adjustment.adjusted) {
				workingStartPos = adjustment.startPos;
				workingUserContentEndPos = adjustment.userContentEndPos;
			}
		} else {
			const adjustment = calculateRangeSelectionAdjustment({
				state,
				fragment,
				startPos: workingStartPos,
				userContentEndPos: workingUserContentEndPos,
			});
			if (adjustment.adjusted) {
				workingStartPos = adjustment.startPos;
			}

			const listAdjustment = calculateListAdjustment({
				state,
				fragment,
				startPos: workingStartPos,
				userContentEndPos: workingUserContentEndPos,
			});
			if (listAdjustment.adjusted) {
				workingStartPos = listAdjustment.startPos;
				workingUserContentEndPos = listAdjustment.userContentEndPos;
			}

		}

		workingPosAdjusted = true;
	}

	// Block mark adjustment — runs on every update (not guarded by posAdjusted)
	// because the first streaming chunk may not yet contain the block marks.
	const blockMarkAdjustment = calculateBlockMarkAdjustment({
		state,
		fragment,
		startPos: workingStartPos,
		userContentEndPos: workingUserContentEndPos,
	});
	if (blockMarkAdjustment.adjusted) {
		workingStartPos = blockMarkAdjustment.startPos;
	}

	// Calculate toPos
	let toPos = Math.min(
		Math.max(aiContentEndPos ?? 0, workingStartPos + slice.size + 1),
		workingUserContentEndPos,
	);

	// Table adjustment
	const tableAdjustment = calculateTableAdjustment({
		state,
		fragment,
		startPos: workingStartPos,
		toPos,
		userContentEndPos: workingUserContentEndPos,
	});
	if (tableAdjustment.adjusted) {
		workingStartPos = tableAdjustment.startPos;
		toPos = tableAdjustment.toPos;
	}

	// Sync block adjustment
	if (expValEquals('platform_editor_aifc_sync_block_stream_fix', 'isEnabled', true)) {
		const syncBlockAdjustment = calculateSyncBlockAdjustment({
			state,
			fragment,
			startPos: workingStartPos,
			toPos,
			userContentEndPos: workingUserContentEndPos,
		});
		if (syncBlockAdjustment.adjusted) {
			workingStartPos = syncBlockAdjustment.startPos;
			toPos = syncBlockAdjustment.toPos;
		}
	}

	// Slice adjustment
	slice = calculateSliceOpenStartEndAdjustment({
		state,
		slice,
		startPos: workingStartPos,
		toPos,
	});

	// Build transaction
	let tr = state.tr;
	const safeToPos = Math.max(workingStartPos, Math.min(toPos, tr.doc.content.size));

	try {
		tr = tr.replaceRange(workingStartPos, safeToPos, slice);
	} catch {
		return buildPositionOnlyUpdate(state, streamingState, responseVersion);
	}

	tr = tr.setMeta('ignoreLocalIdUpdate', true);
	tr = metaTransaction({ tr, isFinalStream: false, stackTime: streamTimeStamp });

	// Map positions AFTER document changes
	const newDocSize = tr.doc.content.size;
	const mappedStartPos = clampPosition(mapStartPos({ pos: workingStartPos, tr }), newDocSize);
	const mappedUserContentEndPos = clampPosition(
		mapToPos({ pos: workingUserContentEndPos, tr }),
		newDocSize,
	);
	const mappedAiContentEndPos = clampPosition(mapToPos({ pos: toPos, tr }), newDocSize);

	// Calculate telepointer
	const telepointerPos = calculateTelepointerPos(tr.doc, mappedStartPos, mappedAiContentEndPos);

	// Use original transform with extended payload
	tr = updateStreamPositions({
		aiContentEndPos: mappedAiContentEndPos,
		telepointerPos,
		isFinalStream: false,
		lastProcessedVersion: responseVersion,
		aiContentSlice: slice,
		scrollTargetPos: mappedAiContentEndPos,
		posAdjusted: workingPosAdjusted,
		startPos: mappedStartPos,
		userContentEndPos: mappedUserContentEndPos,
	})(tr);

	// Fire the loading→complete analytics step once (guarded by lastQueuedStep in AnalyticsBridge).
	// This triggers ACTION.INITIATED, the userFlow 'loading' step event, and sets up aiInteractionID
	// for downstream steps. ACTION.VIEWED is NOT tied to this — it fires on 'complete' (after stream ends).
	fireAnalyticsStep('loading', 'complete');

	return tr;
}

function buildPositionOnlyUpdate(
	state: EditorState,
	streamingState: PurePmInEditorDirectStreamingPluginState,
	responseVersion: number,
): Transaction {
	const { aiContentEndPos, telepointerPos, startPos, aiContentSlice } = streamingState;

	let tr = state.tr;
	tr = updateStreamPositions({
		aiContentEndPos: aiContentEndPos ?? startPos ?? 0,
		telepointerPos: telepointerPos ?? startPos ?? 0,
		isFinalStream: false,
		lastProcessedVersion: responseVersion,
		aiContentSlice,
	})(tr);

	return tr;
}
